import { Directive, Injectable, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { Observable, of } from 'rxjs';
import { delay, map, shareReplay, startWith, takeUntil } from 'rxjs/operators';

import { BaseContainer } from '../../../../core/common/containers/base.abstract.container';
import { ActionItem } from '../../../../elements/components/action-item/entities/action-item.entity';
import { IconSize } from '../../../../elements/components/icon/entities/icon-sizes.entity';
import { ErrorResponse } from '../../../entities/errors-response.entity';
import { FormGroupStyling } from '../../../entities/form-group-styling.entity';
import { IsControlDisabled } from '../../../helpers/control-disabled.helper';
import { IsControlInvalid } from '../../../helpers/control-invalid.helper';
import { IsControlRequired } from '../../../helpers/control-required.helper';
import { LabelIcon } from '../form-label/label-icon.entity';

@Directive()
@Injectable()
export abstract class ShBaseFormComponent extends BaseContainer implements OnInit, OnDestroy {
    sizes: typeof IconSize = IconSize;
    controlId!: string;
    describeLabelId!: string;

    public labelActions$: Observable<ActionItem[]>;

    isControlInvalid$!: Observable<boolean>;
    isControlRequired$!: Observable<boolean>;
    isControlDisabled$!: Observable<boolean>;
    isControlTouched$!: Observable<boolean>;

    @Input() set id(id: string) {
        const randLetter = String.fromCharCode(65 + Math.floor(Math.random() * 26));
        const uniqid = randLetter + Date.now();
        this.controlId = `${id}-${uniqid}`;
    }
    @Input() control!: UntypedFormControl;
    @Input() label?: string;
    @Input() labelIcons: LabelIcon[] = [];
    @Input() actions?: ActionItem[]; // Displayed next to input(s) as button
    @Input() labelActions?: ActionItem[]; // Displayed next to title as buttonlink
    @Input() errorMsg!: string;
    @Input() helpText?: string;
    @Input() canBeIndeterminate: boolean = false;
    @Input() indeterminateMsg!: string;
    @Input() errorResponse: ErrorResponse = ErrorResponse.IMMEDIATE;
    @Input() disabledMsg?: string = '';
    @Input() formGroupStyling: FormGroupStyling[] = [];

    @Input() inOverflownContainer: boolean = false;

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.setDescribeLabelId();
        this.setControlStateStreams();
        this.initLabelActions();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected initLabelActions(): void {
        this.labelActions$ = this.labelActions ? of(this.labelActions) : of([]);
    }

    get currentActionItems(): ActionItem[] | undefined {
        return this.actions && this.actions.length > 0 ? this.actions : undefined;
    }

    private setControlStateStreams(): void {
        this.isControlInvalid$ = this.control.statusChanges.pipe(
            takeUntil(this.destroy$),
            delay(0),
            startWith(this.control.status),
            map(() => IsControlInvalid(this.control, this.errorResponse)),
            shareReplay(1)
        );

        this.isControlRequired$ = this.control.statusChanges.pipe(
            takeUntil(this.destroy$),
            delay(0),
            startWith(this.control.status),
            map(() => IsControlRequired(this.control)),
            shareReplay(1)
        );

        this.isControlDisabled$ = this.control.statusChanges.pipe(
            takeUntil(this.destroy$),
            delay(0),
            startWith(this.control.status),
            map(() => IsControlDisabled(this.control)),
            shareReplay(1)
        );
    }

    private setDescribeLabelId(): void {
        this.describeLabelId = this.id + '-error-description';
    }
}
